import Link from '@gik/core/components/Link.tsx';
import type { Group, GroupHeaderSettings, GroupSettings } from '@gik/core/models/gik/Group';
import type { UIComponent } from '@gik/core/types/UI';
import { useBemCN } from '@gik/core/utils/bemBlock';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { logoImageUrl as logoPlaceholderUrl } from '@gik/inkind-page/constants';
import { useGroupDefaults } from '@gik/inkind-page/hooks/useGroupDefaults';
import { HTMLParser } from '@gik/ui-legacy/HTMLParser';
import { HoverAnimation } from '@gik/ui-legacy/HoverAnimation';
import { Image } from '@gik/ui-legacy/Image';
import type React from 'react';

export type InkindUserGroupCardProps = {
  group: Group;
  muted?: boolean;
  groupSettings?: GroupHeaderSettings;
} & UIComponent;

function InkindUserGroupCardComp({ muted, group: _group, groupSettings }: InkindUserGroupCardProps): JSX.Element {
  const bem = useBemCN('user-group-card');

  const group = useGroupDefaults(_group);
  const isMember = true;

  const settings: Partial<GroupSettings> = groupSettings || group?.inkindGroupSettings;

  if (!settings) return null;
  const hasGradient = settings?.styleVariant === 'gradient';

  // Only ever show a logo the group actually uploaded. A custom logo always
  // renders — over ANY background, including a banner image — matching the
  // widget and the page-banner header. When there is no custom logo we
  // deliberately show nothing on top of the thumbnail (just the selected
  // background: solid colour, gradient, or image). The generic "Your Company"
  // fallback is a branding-preview-only affordance and is never shown here.
  const logo = group?.inkindGroupSettings?.logoImageUrl;
  const hasCustomLogo = !!logo && logo !== logoPlaceholderUrl;

  return (
    <HoverAnimation {...bem('hover-wrapper', null, ['tw-flex'])}>
      <div className="tw-flex tw-flex-1">
        <Link href={`/groups/${group?.wordPressGroupSlug}`} className="tw-flex tw-flex-1">
          <div {...bem(null, [{ admin: isMember }, { muted }, { ['has-gradient']: hasGradient }], ['tw-flex-1'])}>
            <section
              {...bem('image-wrapper')}
              style={
                {
                  // Use `undefined` (not `false`) when a value does not apply —
                  // a boolean CSS value hydrates inconsistently server vs client.
                  // See docs/guides/hydration-troubleshooting.md.
                  '--background-image':
                    settings.styleVariant === 'image' ? `url('${settings.bannerImageUrl}')` : undefined,
                  '--gradient-color-1': settings.gradientColor1,
                  '--gradient-color-2': settings.gradientColor2,
                  backgroundColor:
                    settings.styleVariant === 'solid' || !settings.styleVariant ? settings.brandColor : undefined,
                } as React.CSSProperties
              }
            >
              {hasCustomLogo && <Image {...bem('icon')} src={logo} />}
            </section>

            <section {...bem('content')}>
              <div {...bem('content-inner')}>
                <div>
                  <section {...bem('title')}>
                    <HTMLParser
                      rawHtml={group?.title?.length > 75 ? group?.title?.substr(0, 72) + '...' : group?.title}
                    />
                  </section>
                </div>
              </div>
            </section>
          </div>
        </Link>
      </div>
    </HoverAnimation>
  );
}

export const InkindUserGroupCard = withComponentErrorBoundary(InkindUserGroupCardComp);
